import { Injectable, type OnDestroy } from '@angular/core';
import { AuthService, WorkspaceService } from '@principle-theorem/ng-auth';
import {
  IPartner,
  IPartnerApp,
  IUser,
  RootCollection,
  WorkspaceType,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  filterUndefined,
  find$,
  getDoc$,
  shareReplayCold,
  shareReplayHot,
  where,
  type CollectionReference,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, of, Subject, type Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ManagementService } from '../auth/management.service';
import { Partner } from '@principle-theorem/principle-core';

@Injectable({
  providedIn: 'root',
})
export class PartnerService implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  userCol$: Observable<CollectionReference<IUser>>;
  partner$: Observable<WithRef<IPartner> | undefined>;
  apps$: Observable<WithRef<IPartnerApp>[]>;
  users$: Observable<WithRef<IUser>[]>;
  user$: Observable<WithRef<IUser> | undefined>;
  storagePath$: Observable<string>;

  constructor(
    private _auth: AuthService,
    private _workspace: WorkspaceService,
    private _management: ManagementService
  ) {
    this.partner$ = combineLatest([
      this._auth.authUser$,
      this._workspace.value$,
    ]).pipe(
      switchMap(([user, workspace]) => {
        if (
          !user ||
          !workspace ||
          !workspace.uid ||
          workspace.type !== String(WorkspaceType.Partner)
        ) {
          return of(undefined);
        }

        return getDoc$(Partner.col(), workspace.uid).pipe(
          catchError(() => of(undefined))
        );
      })
    );

    this.apps$ = this.partner$.pipe(
      filterUndefined(),
      switchMap((partner) => Partner.apps$(partner))
    );

    this.userCol$ = this.partner$.pipe(
      filterUndefined(),
      map((partner) => Partner.userCol(partner))
    );

    this.users$ = this.userCol$.pipe(
      switchMap((userCol) => (userCol ? all$(userCol) : of([]))),
      shareReplayHot(this._onDestroy$)
    );

    this.storagePath$ = this.partner$.pipe(
      filterUndefined(),
      map(
        (partner: WithRef<IPartner>) =>
          `${RootCollection.Partners}/${partner.ref.id}`
      )
    );

    this.user$ = this._auth.authUser$.pipe(
      switchMap((authUser) => {
        if (!authUser || !authUser.email) {
          return of(undefined);
        }
        return this.resolveUser$(authUser.email);
      }),
      shareReplayCold()
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  resolveUser$(email: string): Observable<WithRef<IUser> | undefined> {
    return this._management.user$.pipe(
      switchMap((managementUser) => {
        if (managementUser) {
          return of(managementUser);
        }
        return this.userCol$.pipe(
          switchMap((userCol) =>
            userCol
              ? find$(
                  userCol,
                  where('email', '==', email.toLowerCase()),
                  where('isEnabled', '==', true)
                )
              : of(undefined)
          )
        );
      })
    );
  }
}
